import { GraphQLFormattedError } from 'graphql'
import moment from 'moment-timezone'
import { FC, useCallback, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InviteModalContent from '../../../components/custom_components/InviteModalContent'
import { InviteModalContentUtils } from '../../../components/custom_components/InviteModalContent/inviteModalContentUtils'
import { PerModal } from '../../../components/custom_components/Modal'
import { useAuth } from '../../../context/auth-context'
import useAcceptMeetingRequest from '../../../graphql/meeting/hooks/useAcceptMeetingRequest'
import useInviteMeeting, { MeetingInviteProps } from '../../../graphql/meeting/hooks/useInviteMeeting'
import {
  refreshEmployeeSkillStatusInAppraisalInviteModal,
  sendSkillStatusUpdateMail,
} from '../../../graphql/skill-status/useSkillStatusMutations'
import { getValidationErrors, ValidationErrorsData } from '../../../shared/graphqlErrorHelper'
import { doBothTextsHaveSameCharacters, removeAllHtmlTagsAndTrim } from '../../../shared/textHelper'
import {
  AcceptRequestedMeetingMutation,
  AppraisalSeasonDto,
  CreateOtherMeetingInviteMutation,
  EmployeeDisplayTinyInfo,
  EmployeeWithBuFragment,
  InviteToAppraisalMeetingMutation,
  MeetingRequestDto,
  MeetingStepViewId,
  MeetingType,
  SkillEmployeeStatusDto,
  SkipMeetingStepReason,
} from '../../../types'
import { sortSteps } from '../../appraisal/appraisal-flow/meeting/stepSorter'
import { RequestedMeetingModalSubheading } from '../cancel/RequestMeetingDeclineModal'
import {
  fullAppraisalSteps,
  getAppraisalSeasonOptionByEmployeeId,
  getInviteOptionsByEmployeeId,
  getNextStepConfiguration,
  getNonParticipantsOptionsByEmployeeId,
  getSampleAgenda,
  getSampleEmail,
  retrieveSkillStatusDataByEmployeeId,
  updateAppraisalSteps,
  updateEmailAndAgenda,
} from './inviteUtil'
import { FetchResult } from '@apollo/client'
import { showNotification } from '../../../components/custom_components/Notification/notification'

export interface InviteModalState {
  meetingId?: string
  employee: EmployeeDisplayTinyInfo
  meetingDate: string
  isLoadingInviteOptions: boolean
  isLoadingNonParticipantsOptions: boolean
  isLoadingASOptions: boolean
  meetingType: MeetingType
  appraisalSeason?: AppraisalSeasonDto
  inviteOptions?: MeetingType[]
  tagAssignmentIsInProgress?: boolean
  isAppraisalEnabled: boolean
  meetingAgenda?: string
  emailText?: string
  viewIds?: MeetingStepViewId[]
  reasonForReducedSteps?: SkipMeetingStepReason
  serverError?: ValidationErrorsData[]
  loadError?: ValidationErrorsData[]
  isMeetingDateLoadError?: boolean
  location?: string
  duration: number
  additionalParticipants: EmployeeWithBuFragment[]
  additionalParticipantsOptions?: EmployeeWithBuFragment[]
  skillStatusData?: SkillEmployeeStatusDto
  isLoadingSkillStatusData: boolean
}

interface ReducerActionType {
  type:
    | 'update'
    | 'updateSteps'
    | 'reset'
    | 'appraisalSeasonOptionsChanged'
    | 'inviteOptionsChanged'
    | 'nonParticipantsOptionsChanged'
    | 'initStepViewIds'
    | 'skillStatusDataChanged'
    | 'setError'
    | 'setLoadError'
  fieldName?: string
  value?: any
  stepId?: MeetingStepViewId
  error?: ValidationErrorsData[]
  isMeetingDateLoadError?: boolean
  serverError?: null
  location?: string
}
export interface MeetingInviteModalProps {
  visible: boolean
  onCancel: () => void
  updateEmployeeSkillStatus?: (skillStatusData?: SkillEmployeeStatusDto) => void
  employeeList: EmployeeDisplayTinyInfo[]
  requestedMeeting?: MeetingRequestDto
}

export const MeetingInviteModal: FC<MeetingInviteModalProps> = ({
  visible,
  employeeList,
  onCancel,
  requestedMeeting,
  updateEmployeeSkillStatus,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { createMeetingInvite } = useInviteMeeting()
  const { acceptMeetingRequest } = useAcceptMeetingRequest()
  const nextMondayDateUTCString = moment().tz(user.timezone).startOf('isoWeek').add(1, 'week').hours(8).toISOString()
  const meetingDateUTCString = requestedMeeting ? requestedMeeting.requestedDate : nextMondayDateUTCString

  const initialState: InviteModalState = {
    meetingDate: meetingDateUTCString,
    employee: employeeList[0],
    isLoadingInviteOptions: true,
    isLoadingNonParticipantsOptions: true,
    isLoadingASOptions: true,
    serverError: undefined,
    loadError: undefined,
    isMeetingDateLoadError: undefined,
    meetingAgenda: getSampleAgenda(t, requestedMeeting ? requestedMeeting.type : MeetingType.OtherGeneric, employeeList[0]),
    emailText: getSampleEmail(t, requestedMeeting ? requestedMeeting.type : MeetingType.OtherGeneric, employeeList[0], user),
    viewIds: [],
    inviteOptions: [],
    appraisalSeason: requestedMeeting ? requestedMeeting.appraisalSeason : undefined,
    tagAssignmentIsInProgress: undefined,
    isAppraisalEnabled: true,
    meetingType: requestedMeeting ? requestedMeeting.type : MeetingType.OtherGeneric,
    reasonForReducedSteps: undefined,
    location: '',
    duration: 3600,
    additionalParticipantsOptions: [],
    additionalParticipants: [],
    skillStatusData: undefined,
    isLoadingSkillStatusData: true,
  }

  const [isSubmitting, toggleSubmitting] = useState(false)
  const [isRefreshSkillStatusProcessing, toggleRefreshSkillStatusProcessing] = useState<boolean>(false)

  const loadASOptionsCallback = useCallback((value) => {
    dispatch({ type: 'appraisalSeasonOptionsChanged', fieldName: 'asOptions', value: value })
  }, [])

  const loadInviteOptionsCallback = useCallback((value) => {
    dispatch({ type: 'inviteOptionsChanged', fieldName: 'inviteOptions', value: value })
  }, [])

  const loadSkillStatusDataCallback = useCallback((value?: SkillEmployeeStatusDto | null) => {
    dispatch({ type: 'skillStatusDataChanged', fieldName: 'skillStatusData', value: value })
  }, [])

  const loadNonParticipantsOptionsCallback = useCallback((value) => {
    dispatch({ type: 'nonParticipantsOptionsChanged', fieldName: 'additionalParticipantsOptions', value: value })
  }, [])

  const setLoadErrorCallback = (errors?: ReadonlyArray<GraphQLFormattedError>, isMeetingDateLoadError = false) => {
    if (errors) {
      dispatch({
        type: 'setLoadError',
        error: getValidationErrors(errors),
        isMeetingDateLoadError,
      })
    }
  }

  const setMeetingDateLoadErrorCallback = (errors?: ReadonlyArray<GraphQLFormattedError>) => {
    setLoadErrorCallback(errors, true)
  }

  // initial fetch of options
  useEffect(() => {
    getAppraisalSeasonOptionByEmployeeId(employeeList[0].id, meetingDateUTCString, loadASOptionsCallback, setLoadErrorCallback)
    if (requestedMeeting) {
      dispatch({ type: 'initStepViewIds' })
    }
    getNonParticipantsOptionsByEmployeeId(employeeList[0].id, loadNonParticipantsOptionsCallback, setLoadErrorCallback)
    retrieveSkillStatusDataByEmployeeId(employeeList[0].id, loadSkillStatusDataCallback, setLoadErrorCallback)
    // eslint-disable-next-line
  }, [employeeList, visible])

  useEffect(() => {
    if (isRefreshSkillStatusProcessing) {
      refreshEmployeeSkillStatusInAppraisalInviteModal(state.employee.id)
        .then((res) => {
          dispatch({
            type: 'skillStatusDataChanged',
            fieldName: 'skillStatusData',
            value: res.data.retrieveSkillStatusDataInMeetingInvitation.skillEmployeeStatusDto,
          })
          toggleRefreshSkillStatusProcessing(false)
        })
        .catch((err) => {
          dispatch({
            type: 'setError',
            error: err,
          })
          toggleRefreshSkillStatusProcessing(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshSkillStatusProcessing])

  const inviteModalContentUtils = new InviteModalContentUtils({
    timeZoneAndLocale: user,
    appraisalSeason: requestedMeeting ? requestedMeeting.appraisalSeason : undefined,
  })

  const reducer = (state: InviteModalState, action: ReducerActionType) => {
    let nextState = { ...state }
    switch (action.type) {
      case 'reset':
        getAppraisalSeasonOptionByEmployeeId(initialState.employee.id, meetingDateUTCString, loadASOptionsCallback, setLoadErrorCallback)
        getNonParticipantsOptionsByEmployeeId(initialState.employee.id, loadNonParticipantsOptionsCallback, setLoadErrorCallback)
        nextState.serverError = []
        nextState.loadError = undefined
        nextState.isMeetingDateLoadError = undefined
        toggleSubmitting(false)
        return initialState

      case 'update':
        const { fieldName, value } = action
        // update field first
        // this can be used to update any field that doesn't use any value transformation(or some other complex logic)
        if (fieldName) {
          nextState[fieldName] = value
        }

        // update depending fields next
        if (fieldName === 'employee') {
          nextState.isLoadingInviteOptions = true
          nextState.isLoadingASOptions = true
          nextState.isLoadingNonParticipantsOptions = true
          nextState.isLoadingSkillStatusData = true
          nextState.loadError = undefined
          nextState.isMeetingDateLoadError = undefined
          nextState.serverError = undefined

          // load options
          getAppraisalSeasonOptionByEmployeeId(value.id, meetingDateUTCString, loadASOptionsCallback, setLoadErrorCallback)
          getNonParticipantsOptionsByEmployeeId(value.id, loadNonParticipantsOptionsCallback, setLoadErrorCallback)
          retrieveSkillStatusDataByEmployeeId(nextState.employee.id, loadSkillStatusDataCallback, setLoadErrorCallback)
          //clean fields

          nextState.meetingDate = meetingDateUTCString
          nextState.emailText = ''
          nextState.meetingAgenda = ''
          nextState.meetingType = MeetingType.OtherGeneric
          nextState = updateEmailAndAgenda(t, nextState, MeetingType.OtherGeneric, nextState.employee, user)
        } else if (fieldName === 'meetingType') {
          nextState = updateEmailAndAgenda(t, nextState, nextState.meetingType, nextState.employee, user)
          if (value === MeetingType.Appraisal) {
            nextState.viewIds = fullAppraisalSteps
          } else if (value === MeetingType.AppraisalUpdate) {
            nextState.viewIds = updateAppraisalSteps
          }
        } else if (fieldName === 'meetingDate') {
          const hasMeetingDateLoadError = !!(state.loadError && state.loadError.length && state.isMeetingDateLoadError)
          const isDateInCurrentSeason =
            !!value &&
            !!state.appraisalSeason &&
            inviteModalContentUtils.isMeetingDateInAppraisalSeasonBoundaries(moment(value), state.appraisalSeason)

          if (hasMeetingDateLoadError && isDateInCurrentSeason) {
            nextState.loadError = undefined
            nextState.isMeetingDateLoadError = undefined
          } else if (value && (hasMeetingDateLoadError || !state.appraisalSeason || !isDateInCurrentSeason)) {
            nextState.isLoadingASOptions = true
            nextState.isLoadingInviteOptions = true
            getAppraisalSeasonOptionByEmployeeId(nextState.employee.id, value, loadASOptionsCallback, setMeetingDateLoadErrorCallback)
          }
        }

        break

      case 'updateSteps':
        nextState.viewIds = getNextStepConfiguration(action.stepId!, state.meetingType, state.viewIds)
        break

      case 'appraisalSeasonOptionsChanged':
        nextState.loadError = undefined
        nextState.isMeetingDateLoadError = undefined
        const appraisalSeason: AppraisalSeasonDto = action.value
        if (nextState.appraisalSeason === appraisalSeason) {
          nextState.isLoadingASOptions = false
          nextState.isLoadingSkillStatusData = false
          break
        }

        // set options
        nextState.appraisalSeason = appraisalSeason
        nextState.isLoadingASOptions = false
        getInviteOptionsByEmployeeId(nextState.employee.id, loadInviteOptionsCallback, setLoadErrorCallback, nextState.appraisalSeason.id)
        break

      case 'inviteOptionsChanged':
        let inviteOptions: MeetingType[] = action.value.possible
        let selectedOption: MeetingType | undefined

        if (!!requestedMeeting) {
          inviteOptions = [requestedMeeting.type]
        }

        if (inviteOptions.length === 1) {
          selectedOption = inviteOptions[0]
        } else {
          selectedOption = inviteOptions.find((option: MeetingType) => {
            return (
              [MeetingType.AppraisalUpdate, MeetingType.Appraisal].includes(option) &&
              !action.value.tagAssignmentIsInProgress &&
              action.value.isAppraisalEnabled
            )
          })
          if (!selectedOption) {
            selectedOption = inviteOptions.find((option: MeetingType) => option === MeetingType.OtherGeneric)
            if (!selectedOption) {
              const selectableOptions = inviteOptions.filter(
                (option: MeetingType) => option !== MeetingType.Appraisal && option !== MeetingType.AppraisalUpdate,
              )
              selectedOption = selectableOptions[0]
            }
          }
        }
        nextState.inviteOptions = inviteOptions
        nextState.tagAssignmentIsInProgress = action.value.tagAssignmentIsInProgress
        nextState.isAppraisalEnabled = action.value.isAppraisalEnabled
        if (selectedOption === state.meetingType) {
          nextState.isLoadingInviteOptions = false
          break
        }

        // set options
        nextState.meetingType = selectedOption
        nextState.isLoadingInviteOptions = false

        // get depending info
        nextState = updateEmailAndAgenda(t, nextState, selectedOption, nextState.employee, user)

        if (selectedOption === MeetingType.Appraisal) {
          nextState.viewIds = fullAppraisalSteps
        }

        if (selectedOption === MeetingType.AppraisalUpdate) {
          nextState.viewIds = updateAppraisalSteps
        }
        break

      case 'nonParticipantsOptionsChanged':
        nextState.additionalParticipants = []
        nextState.additionalParticipantsOptions = action.value
        nextState.isLoadingNonParticipantsOptions = false
        break

      case 'initStepViewIds':
        if (requestedMeeting && requestedMeeting.type === MeetingType.Appraisal) {
          nextState.viewIds = fullAppraisalSteps
        }

        if (requestedMeeting && requestedMeeting.type === MeetingType.AppraisalUpdate) {
          nextState.viewIds = updateAppraisalSteps
        }
        break

      case 'skillStatusDataChanged':
        nextState.skillStatusData = action.value || undefined
        nextState.isLoadingSkillStatusData = false
        break

      case 'setError':
        if (action.error) {
          nextState.serverError = action.error as ValidationErrorsData[]
        }
        break

      case 'setLoadError':
        nextState.isLoadingInviteOptions = false
        nextState.isLoadingASOptions = false
        nextState.isLoadingNonParticipantsOptions = false
        nextState.isLoadingSkillStatusData = false
        if (action.error) {
          nextState.loadError = action.error as ValidationErrorsData[]
          nextState.isMeetingDateLoadError = !!action.isMeetingDateLoadError
        }
        break
      default:
        throw new Error()
    }
    return nextState
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  const saveInvitation = async () => {
    toggleSubmitting(true)
    const sanitizedLocation = !!state.location ? (state.location.trim().length > 0 ? state.location.trim() : undefined) : undefined
    const inviteData: MeetingInviteProps = {
      employeeId: state.employee.id,
      appraisalSeasonId: state.appraisalSeason!.id,
      reviewerId: user.employeeId,
      meetingDate: state.meetingDate,
      meetingType: state.meetingType,
      viewIds: sortSteps(state.viewIds ? state.viewIds : [], state.meetingType === MeetingType.AppraisalUpdate),
      meetingAgenda: state.meetingAgenda,
      emailText: state.emailText,
      reasonForReducedSteps: appraisalWithReducedSteps ? state.reasonForReducedSteps : undefined,
      location: sanitizedLocation,
      duration: state.duration,
      additionalParticipantsIds: state.additionalParticipants.map((e) => e.id),
    }
    let result:
      | FetchResult<AcceptRequestedMeetingMutation>
      | {
          data: InviteToAppraisalMeetingMutation | CreateOtherMeetingInviteMutation | null | undefined
          errors: readonly GraphQLFormattedError<Record<string, any>>[] | undefined
        }
    if (requestedMeeting) {
      result = await acceptMeetingRequest(requestedMeeting.id, inviteData)
    } else {
      result = await createMeetingInvite(inviteData)
    }

    if (result && !result.errors) {
      const notificationType = requestedMeeting ? 'accept' : 'invite'
      showNotification({
        type: 'success',
        message: t(`meetingInvite.notifications.${notificationType}.success.title`),
        description: t(`meetingInvite.notifications.${notificationType}.success.description`, {
          employeeName: state.employee.firstName,
        }),
      })
      updateSkillStatusTableRow()
    } else {
      toggleSubmitting(false)
      dispatch({
        type: 'setError',
        error: result.errors && result.errors[0] && result.errors[0].extensions ? result.errors[0].extensions.validationErrors : undefined,
      })
    }
  }

  const sendSkillStatusUpdateMailFunction = async () => {
    toggleSubmitting(true)
    const result = await sendSkillStatusUpdateMail(state.employee.id)

    if (result && !result.errors) {
      updateSkillStatusTableRow()
    } else {
      toggleSubmitting(false)
      dispatch({
        type: 'setError',
        error:
          result.errors && result.errors[0] && result.errors[0].extensions
            ? (result.errors[0].extensions.validationErrors as ValidationErrorsData[])
            : undefined,
      })
    }
  }

  const containsStepViewId = (stepId: MeetingStepViewId): boolean => {
    if (state.viewIds) {
      return !!state.viewIds.find((x) => x === stepId)
    }
    return false
  }

  const isAppraisalMeeting = !state.isLoadingInviteOptions ? state.meetingType === MeetingType.Appraisal : false
  const isAppraisalUpdateMeeting = !state.isLoadingInviteOptions ? state.meetingType === MeetingType.AppraisalUpdate : false
  const isOtherMeeting = !(isAppraisalMeeting || isAppraisalUpdateMeeting)

  const appraisalWithReducedSteps =
    isAppraisalMeeting && !(containsStepViewId(MeetingStepViewId.SelfReflection) && containsStepViewId(MeetingStepViewId.Evaluation))

  const invalidAppraisal = appraisalWithReducedSteps && !state.reasonForReducedSteps
  const invalidAppraisalUpdate = isAppraisalUpdateMeeting && (!state.viewIds || state.viewIds.length === 0)
  const invalidOtherMeeting = !state.isLoadingInviteOptions
    ? isOtherMeeting &&
      (!removeAllHtmlTagsAndTrim(state.meetingAgenda ? state.meetingAgenda : '') ||
        !removeAllHtmlTagsAndTrim(state.emailText ? state.emailText : ''))
    : false

  const meetingAgendaNotChanged =
    (state.meetingType === MeetingType.OtherGeneric || state.meetingType === MeetingType.OtherProjectFeedback) &&
    doBothTextsHaveSameCharacters(getSampleAgenda(t, state.meetingType, state.employee), state.meetingAgenda ? state.meetingAgenda : '')
  const emailTextNotChanged =
    (state.meetingType === MeetingType.OtherGeneric || state.meetingType === MeetingType.OtherProjectFeedback) &&
    doBothTextsHaveSameCharacters(getSampleEmail(t, state.meetingType, state.employee, user), state.emailText ? state.emailText : '')

  const invalidDateTime =
    state.meetingDate === '' ||
    state.meetingDate === null ||
    state.meetingDate === undefined ||
    inviteModalContentUtils.isMeetingDateNotAllowed(state.meetingDate)

  const disabledInviteButton =
    invalidAppraisalUpdate ||
    invalidAppraisal ||
    invalidOtherMeeting ||
    emailTextNotChanged ||
    meetingAgendaNotChanged ||
    isSubmitting ||
    invalidDateTime ||
    (state.serverError && state.serverError.length > 0) ||
    (!!requestedMeeting && !requestedMeeting.isRequestValid)

  const disabledInviteButtonTooltip = () => {
    if (!!requestedMeeting && !requestedMeeting.isRequestValid) {
      return t(`meetingInvite.invalidMeetingRequest`)
    } else if (emailTextNotChanged && meetingAgendaNotChanged) {
      return t(`meetingInvite.doScheduleDisabledEmailAndAgendaTooltip`)
    } else if (meetingAgendaNotChanged) {
      return t(`meetingInvite.doScheduleDisabledAgendaTooltip`)
    } else if (emailTextNotChanged) {
      return t(`meetingInvite.doScheduleDisabledEmailTooltip`)
    } else if (invalidDateTime) {
      return t(`meetingInvite.invalidDateTimeTooltip`)
    } else {
      return undefined
    }
  }

  const updateSkillStatusTableRow = () => {
    if (updateEmployeeSkillStatus) {
      updateEmployeeSkillStatus(state.skillStatusData)
    }
    dispatch({ type: 'reset' })
    onCancel()
  }

  const isSkillStatusBlocker = false /*isSkillStatusBlockerInInvitation(state.skillStatusData)*/
  return (
    <PerModal
      visible={visible}
      onCancel={() => {
        updateSkillStatusTableRow()
      }}
      size="large"
      title={{
        heading: requestedMeeting ? t('meetingView.requestedMeetings.modal.accept.title') : t(`meetingInvite.headline`),
        subheading: requestedMeeting && (
          <RequestedMeetingModalSubheading requester={requestedMeeting.requester} meetingType={requestedMeeting.type} />
        ),
      }}
      footer={
        state.loadError && state.loadError.length > 0
          ? {
              justify: 'start',
              customBtns: [
                {
                  onClick: () => updateSkillStatusTableRow(),
                  text: t(`global.button.close`),
                  color: 'secondary',
                  icon: 'close_stroke',
                },
              ],
            }
          : {
              justify: 'end',
              customBtns: [
                state.skillStatusData && isSkillStatusBlocker
                  ? {
                      text: t(`meetingInvite.requestSkillUpdate`),
                      onClick: async (e) => {
                        e.preventDefault() // We should refactor button to not emit native event!
                        await sendSkillStatusUpdateMailFunction()
                      },
                      color: 'primary',
                      icon: 'invite_fill',
                      disabled: isSubmitting,
                      isProcessing: { active: isSubmitting },
                      tooltip: t(`meetingInvite.requestSkillUpdateButtonTooltip`),
                      visible: !state.serverError,
                      dataE2eAttr: 'sendSkillStatusUpdateEmail',
                    }
                  : {
                      text: t(`meetingInvite.doScheduleButton`),
                      onClick: async (e) => {
                        e.preventDefault() // We should refactor button to not emit native event!
                        await saveInvitation()
                      },
                      color: 'primary',
                      icon: 'calendar_fill',
                      disabled: disabledInviteButton || isSkillStatusBlocker,
                      isProcessing: { active: isSubmitting },
                      tooltip: isSkillStatusBlocker ? t('meetingInvite.unknownSkillStatus') : disabledInviteButtonTooltip(),
                      visible: !state.serverError,
                      dataE2eAttr: 'invite',
                    },
              ],
            }
      }
    >
      {visible && (
        <>
          <InviteModalContent
            employeeList={employeeList}
            state={state}
            timelocal={user}
            inviteModalContentUtils={inviteModalContentUtils}
            onUpdate={(fieldName, value) => dispatch({ type: 'update', fieldName: fieldName, value: value })}
            onUpdateSteps={(stepId, value) => dispatch({ type: 'updateSteps', stepId: stepId, value: value })}
            requestedMeeting={requestedMeeting}
            isRefreshSkillStatusProcessing={isRefreshSkillStatusProcessing}
            toggleRefreshSkillStatusProcessing={toggleRefreshSkillStatusProcessing}
            serverErrors={state.serverError}
          />
        </>
      )}
    </PerModal>
  )
}
